import React, { FC, useContext, useEffect, useState } from 'react';

import { gql, useLazyQuery, useQuery } from '@apollo/client';
import Select, { components } from 'react-select';
import styled, { css } from 'styled-components';
import Toggle from './Toogle';
import LocationContext from './Context';
import Button from './Button';
import { ReactComponent as IconDown } from '../assets/icons/down-arrow.svg';

interface FiltersProps {
    isMapPage?: boolean;
    setShowFilters?: any;
    noResults?: boolean;
}

interface GroupProps {
    readonly half?: boolean;
}

export const StyledCardMessage = styled.div`
    padding: 16px;
    border: 2px solid #fff;
    background-color: #fff;
    border-radius: 3px;
    margin: 0 16px;
    font-size: 16px;
    line-height: 18px;
`;

const StyledClearButton = styled.div`
    ${({ theme }) => css`
        @media (min-width: 340px) {
            position: absolute;
            right: 24px;
            bottom: 24px;
        }
        @media (min-width: ${theme.breakpoints.sm}px) {
            right: 48px;
            bottom: 48px;
        }
    `}
`;
const StyledGroup = styled.div<GroupProps>`
    ${({ half, theme }) => css`
        position: relative;
        flex-basis: 100%;
        width: 100%;
        margin-bottom: 24px;
        ${half &&
        css`
            @media (min-width: ${theme.breakpoints.sm}px) {
                flex-basis: calc(50% - 10px);
                width: calc(50% - 10px);
            }
        `}
    `}
`;

const StyledLabel = styled.span`
    font-size: 16px;
    font-weight: bold;
    color: #222222;
    letter-spacing: -0.13px;
    line-height: 26px;
    margin-bottom: 8px;
    display: block;
`;
const StyledFilters = styled.div`
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
`;

const StyledToggle = styled(Toggle)`
    align-items: flex-start;
`;

const GET_BRANDS = gql`
    query GetBrands {
        brands(first: 100) {
            nodes {
                databaseId
                name
            }
        }
    }
`;
const GET_PRODUCTS = gql`
    query GetBrandProducts($brand: ID!) {
        brand(id: $brand, idType: DATABASE_ID) {
            databaseId
            products(first: 300) {
                nodes {
                    databaseId
                    title
                }
            }
        }
    }
`;

const CustomDropdownIndicator = ({ innerProps }) => <IconDown {...innerProps} />;
const CustomOption = (props) => <components.Option className="needsclick" {...props} />;
const customStyles = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? '#009FE3' : '#cccccc',
        minHeight: '48px',
        borderWidth: '2px',
    }),
    input: (provided) => ({
        ...provided,
        fontSize: '16px',
    }),

    indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
    }),

    dropdownIndicator: (provided, state) => ({
        ...provided,
        backgroundColor: state.isDisabled ? '#888888' : '#009fe3',
        color: 'white',
        minHeight: '48px',
        minWidth: '48px',
        margin: '-2px',
        borderRadius: '3px',
        justifyContent: 'center',
        alignItems: 'center',
    }),
    singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';

        const fontSize = '16px';
        const lineHeight = '18px';

        return { ...provided, opacity, transition, lineHeight, fontSize };
    },
};
const Filters: FC<FiltersProps> = ({ isMapPage, setShowFilters, noResults }) => {
    const { filters, setFilters } = useContext(LocationContext);
    const [prevFilters, setPrevFilters] = useState<any>();
    const [fireUpdateSearch, setFireUpdateSearch] = useState<boolean | undefined>();
    const { loading: brandsLoading, error: brandsError, data: brandsData } = useQuery(GET_BRANDS);

    const [loadProducts, { loading: productsLoading, error: productsError, data: productData }] = useLazyQuery(
        GET_PRODUCTS
    );

    useEffect(() => {
        setFireUpdateSearch((state) => state);

        setFilters((state) => {
            const newState = { ...state };
            delete newState.product;
            
            if (isMapPage) {
                // Bit of a hack, but we don't want the update search to fire when page is loading.
                setTimeout(() => {
                    setFireUpdateSearch(true);
                }, 1000);
            }

            return newState;
        });
        if (filters?.brand) {
            loadProducts({
                variables: {
                    brand: filters.brand.value,
                },
            });
        }
    }, [filters?.brand, loadProducts, setFilters]);

    useEffect(() => {
        if (
            isMapPage &&
            filters &&
            filters.constructor === Object &&
            Object.keys(filters).length > 0 &&
            JSON.stringify(prevFilters) !== JSON.stringify(filters)
        ) {
            const filterItems: string[] = [];

            if (filters?.stockistType === 'GARDEN_CENTRE') {
                filterItems.push('garden centres only');
            }
            if (filters?.brand?.label) {
                filterItems.push(filters.brand.label);
            }
            if (filters?.product?.label) {
                filterItems.push(filters.product.label);
            }

            if (fireUpdateSearch) {
                window?.dataLayer?.push({
                    event: 'find_a_stockist',
                    action: 'update filter',
                    page_path: '/stockists/map',
                    filters: filterItems.length >= 1 ? filterItems.join(' - ') : '',
                });
            }
            
            setPrevFilters(filters);
        }
    }, [filters]);

    const brands =
        brandsData && brandsData.brands
            ? [
                  { value: '', label: 'Any Brand' },
                  ...brandsData.brands.nodes.map((itm) => ({ value: itm.databaseId, label: itm.name })),
              ]
            : [];
    const products =
        filters?.brand && productData && productData.brand?.products?.nodes
            ? [
                  { value: '', label: 'Any Product' },
                  ...productData.brand.products.nodes.map((itm) => ({ value: itm.databaseId, label: itm.title })),
              ]
            : [];

    const handleChangeFilter = (filter, value) => {
        setFilters((state) => ({
            ...state,
            [filter]: value,
        }));
    };
    const handleClearFilters = () => {
        setFilters(undefined);
    };

    return (
        <StyledFilters>
            <StyledGroup>
                <StyledLabel>Stockist type</StyledLabel>
                <StyledToggle
                    checked={filters?.stockistType === 'GARDEN_CENTRE'}
                    onChange={() =>
                        handleChangeFilter(
                            'stockistType',
                            filters?.stockistType === 'GARDEN_CENTRE' ? '' : 'GARDEN_CENTRE'
                        )
                    }
                    labelRight="Show only Garden Centres"
                    value="GARDEN_CENTRE"
                />
            </StyledGroup>

            <StyledGroup half={!isMapPage}>
                <StyledLabel>Brand</StyledLabel>
                <Select
                    defaultValue={filters?.brand ? filters.brand : ''}
                    value={filters?.brand ? filters.brand : ''}
                    onChange={(sel) => handleChangeFilter('brand', sel)}
                    options={brands}
                    isClearable
                    isSearchable
                    isLoading={brandsLoading}
                    placeholder="Select a brand"
                    styles={customStyles}
                    components={{ DownChevron: CustomDropdownIndicator, Option: CustomOption }}
                />
                {brandsError && <p>Brands failed to load</p>}
            </StyledGroup>
            <StyledGroup half={!isMapPage}>
                <StyledLabel>Product</StyledLabel>
                <Select
                    defaultValue={filters?.product ? filters.product : ''}
                    value={filters?.product ? filters.product : ''}
                    onChange={(sel) => handleChangeFilter('product', sel)}
                    options={products}
                    isClearable
                    isSearchable
                    isLoading={productsLoading}
                    placeholder="Select a product"
                    styles={customStyles}
                    isDisabled={filters && !filters.brand}
                    components={{ DownChevron: CustomDropdownIndicator, Option: CustomOption }}
                />
                {productsError && <p>Products failed to load</p>}
            </StyledGroup>

            {filters && setShowFilters && Object.keys(filters).length >= 1 && (
                <StyledGroup>
                    <Button label="Confirm" onClick={() => setShowFilters(false)} alignSelf="center" fullWidth />
                </StyledGroup>
            )}
            {filters && Object.keys(filters).length >= 1 && (
                <StyledClearButton>
                    <Button color="link" label="Clear all" onClick={handleClearFilters} size="small" />
                </StyledClearButton>
            )}
            {noResults && (
                <StyledCardMessage>
                    <b>Sorry no results found,</b> please try searching again.
                </StyledCardMessage>
            )}
            {/* <pre>{JSON.stringify(products, null, 2)}</pre>
            <pre>{JSON.stringify(brandsError, null, 2)}</pre>
            <pre>{JSON.stringify(productsError, null, 2)}</pre> */}
        </StyledFilters>
    );
};

export default Filters;
